// Product video carousel: Swiper navigation plus click-to-play YouTube cards.
// Without this script the track scrolls natively and each play button links to YouTube.
const YOUTUBE_ID = /^[\w-]{11}$/;
const YOUTUBE_EMBED_ORIGIN = 'https://www.youtube-nocookie.com';

class ProductVideoCarousel extends HTMLElement {
  #swiper = null;
  #abort = null;
  #mounting = false;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.addEventListener('click', this.#onClick, { signal });
    // The theme editor dispatches block events on the block element; they bubble up to here.
    this.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    this.#upgradeCovers(signal);
    this.#mount();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
    this.removeAttribute('data-ready');
  }

  get #slides() {
    return [...this.querySelectorAll('.product-video-carousel__slide')];
  }

  async #mount() {
    const viewport = this.querySelector('[data-viewport]');
    if (!viewport || this.#swiper || this.#mounting || typeof window.loadSwiper !== 'function') return;

    this.#mounting = true;
    try {
      const Swiper = await window.loadSwiper();
      if (!this.isConnected || this.#swiper) return;

      const prevEl = this.querySelector('[data-prev]');
      const nextEl = this.querySelector('[data-next]');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // Figma opens Pad and PC on a card with a neighbour on each side; mobile starts at the first card.
      const centered = window.matchMedia('(min-width: 768px)').matches;
      const initialSlide = centered && this.#slides.length >= 3 ? 1 : 0;

      // Drop the native-scroll fallback (gap, overflow) first so Swiper measures the layout it will drive.
      this.setAttribute('data-ready', '');
      this.#swiper = new Swiper(viewport, {
        initialSlide,
        slidesPerView: 'auto',
        spaceBetween: 12,
        speed: reducedMotion ? 0 : 400,
        watchOverflow: true,
        navigation: prevEl && nextEl ? { prevEl, nextEl } : false,
        a11y: {
          enabled: true,
          // Swiper's default 'group' role would strip the list semantics from the <li> slides.
          slideRole: 'listitem',
          prevSlideMessage: prevEl?.getAttribute('aria-label') || 'Previous',
          nextSlideMessage: nextEl?.getAttribute('aria-label') || 'Next',
        },
        breakpoints: {
          768: { centeredSlides: true, spaceBetween: 16 },
          // PC moves with the arrows only; Pad and mobile keep touch swiping.
          1280: { centeredSlides: true, spaceBetween: 20, allowTouchMove: false },
        },
        on: {
          slideChange: () => this.#pauseVideos(),
        },
      });

      const controls = this.querySelector('[data-controls]');
      if (controls) controls.hidden = false;
    } catch (error) {
      // Leave the native scroll fallback in place; nothing else depends on Swiper.
      if (!this.#swiper) this.removeAttribute('data-ready');
      console.warn('product-video-carousel: Swiper unavailable', error);
    } finally {
      this.#mounting = false;
    }
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const slide = event.target.closest('.product-video-carousel__slide');
    if (!slide || !this.contains(slide)) return;

    const play = event.target.closest('[data-video-play]');
    const index = this.#slides.indexOf(slide);

    // A card that is not the active one first moves into place.
    if (this.#swiper && index !== this.#swiper.activeIndex) this.#swiper.slideTo(index);

    if (play) {
      event.preventDefault();
      this.#play(play.closest('[data-video-id]'));
    }
  };

  #onBlockSelect = (event) => {
    if (!(event.target instanceof Element) || !this.#swiper) return;
    const slide = event.target.closest('.product-video-carousel__slide');
    const index = slide ? this.#slides.indexOf(slide) : -1;
    if (index >= 0) this.#swiper.slideTo(index);
  };

  #play(media) {
    const id = media?.dataset.videoId;
    if (!media || !YOUTUBE_ID.test(id || '') || media.querySelector('iframe')) return;

    this.#pauseVideos();
    const params = new URLSearchParams({
      autoplay: '1',
      playsinline: '1',
      rel: '0',
      enablejsapi: '1',
      origin: window.location.origin,
    });
    const iframe = document.createElement('iframe');
    iframe.className = 'product-video-card__frame';
    iframe.src = `${YOUTUBE_EMBED_ORIGIN}/embed/${id}?${params}`;
    iframe.title = media.dataset.videoTitle || id;
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    media.append(iframe);
    media.closest('.product-video-card')?.setAttribute('data-playing', '');
    iframe.focus();
  }

  // Pauses any playing embed when the carousel moves; needs enablejsapi=1 on the iframe.
  #pauseVideos() {
    this.querySelectorAll('.product-video-card__frame').forEach((frame) => {
      frame.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
        YOUTUBE_EMBED_ORIGIN
      );
    });
  }

  // Swaps the default 480px thumbnail for maxresdefault once the lazy image actually loads,
  // only when YouTube has one (missing ones come back as a 120px placeholder).
  #upgradeCovers(signal) {
    this.querySelectorAll('img[data-youtube-cover]').forEach((img) => {
      const id = img.closest('[data-video-id]')?.dataset.videoId;
      if (!YOUTUBE_ID.test(id || '')) return;

      const upgrade = () => {
        const probe = new Image();
        probe.addEventListener(
          'load',
          () => {
            if (signal.aborted || probe.naturalWidth <= 120) return;
            img.removeAttribute('srcset');
            img.src = probe.src;
          },
          { once: true }
        );
        probe.src = `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;
      };

      if (img.complete && img.naturalWidth > 0) {
        upgrade();
      } else {
        img.addEventListener('load', upgrade, { once: true, signal });
      }
    });
  }
}

if (!customElements.get('product-video-carousel')) {
  customElements.define('product-video-carousel', ProductVideoCarousel);
}
